import {makeAutoObservable} from 'mobx';

/**
 * Represents a service for managing order-related data.
 * This class provides methods to set various properties related to an order, such as frame size, color, personal information, and acceptance of terms.
 */
class OrderDataService {
    /**
     * The selected frame size for the order.
     */
    frameSize?: string;

    /**
     * The selected color for the order.
     */
    color?: string;

    /**
     * The selected cockpit type for the order.
     */
    cockpit?: string;

    /**
     * The family name of the customer.
     */
    familyName?: string;

    /**
     * The given name of the customer.
     */
    givenName?: string;

    /**
     * The street address of the customer.
     */
    street?: string;

    /**
     * The zip code of the customer's address.
     */
    zipCode?: string;

    /**
     * The city of the customer's address.
     */
    city?: string;

    /**
     * The country of the customer's address.
     */
    country?: string;

    /**
     * The company name if the address is a company address.
     */
    companyName?: string;

    /**
     * Indicates whether the address is a company address.
     */
    isCompanyAddress?: boolean;

    /**
     * The date of birth of the customer.
     */
    dateOfBirth?: string;

    /**
     * The email address of the customer.
     */
    email?: string;

    /**
     * The telephone number of the customer.
     */
    telephone?: string;

    /**
     * The timeslot for when the customer is reachable.
     */
    timeslot?: string;

    /**
     * Indicates whether the customer has accepted to receive newsletters.
     */
    hasAcceptedNewsletter = false;

    /**
     * Indicates whether the customer has accepted the legal terms.
     */
    hasAcceptedLegalTerms = false;

    /**
     * Initializes a new instance of the OrderDataService class.
     * This constructor initializes the class instance and makes the instance observable for reactive programming.
     */
    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    /**
     * `canProceedToOrder` is a getter that checks whether the frame size, color and cockpit fit have been set.
     *
     * @returns It returns true if the values are set, false otherwise.
     */
    get canProceedToOrder() {
        return Boolean(this.frameSize) && Boolean(this.color) && Boolean(this.cockpit) && this.color === 'black';
    }

    /**
     * `canOrder` is a getter that checks whether the data has been sufficiently set to allow the creation of an order.
     *
     * @returns It returns true if the values are set, false otherwise.
     */
    get canOrder() {
        return this.canProceedToOrder
            && Boolean(this.familyName)
            && Boolean(this.givenName)
            && Boolean(this.street)
            && Boolean(this.zipCode)
            && Boolean(this.city)
            && Boolean(this.country)
            // Returns false if isCompanyAddress is true but Boolean(this.companyName) is false
            && !(this.isCompanyAddress && !this.companyName)
            && Boolean(this.dateOfBirth)
            && Boolean(this.email)
            && Boolean(this.telephone)
            && this.hasAcceptedLegalTerms;
    }

    /**
     * Sets the frame size for the order.
     *
     * @param frameSize The selected frame size.
     */
    setFrameSize(frameSize: string) {
        this.frameSize = frameSize;
    }

    /**
     * Sets the color for the order.
     *
     * @param color The selected color.
     */
    setColor(color: string) {
        this.color = color;
    }

    /**
     * Sets the cockpit type for the order.
     *
     * @param cockpit The selected cockpit type.
     */
    setCockpit(cockpit: string) {
        this.cockpit = cockpit;
    }

    /**
     * Sets the family name of the customer.
     *
     * @param familyName The family name of the customer.
     */
    setFamilyName(familyName: string) {
        this.familyName = familyName;
    }

    /**
     * Sets the given name of the customer.
     *
     * @param givenName The given name of the customer.
     */
    setGivenName(givenName: string) {
        this.givenName = givenName;
    }

    /**
     * Sets the street address of the customer.
     *
     * @param street The street address of the customer.
     */
    setStreet(street: string) {
        this.street = street;
    }

    /**
     * Sets the zip code of the customer's address.
     *
     * @param zipCode The zip code of the customer's address.
     */
    setZipCode(zipCode: string) {
        this.zipCode = zipCode;
    }

    /**
     * Sets the city of the customer's address.
     *
     * @param city The city of the customer's address.
     */
    setCity(city: string) {
        this.city = city;
    }

    /**
     * Sets the country of the customer's address.
     *
     * @param country The country of the customer's address.
     */
    setCountry(country: string) {
        this.country = country;
    }

    /**
     * Sets the company name.
     *
     * @param companyName The company name.
     */
    setCompanyName(companyName: string) {
        this.companyName = companyName;
    }

    /**
     * Sets whether the address is a company address.
     *
     * @param isCompanyAddress Indicates whether the address is a company address.
     */
    setIsCompanyAddress(isCompanyAddress: boolean) {
        if (!isCompanyAddress) {
            this.companyName = undefined;
        }

        this.isCompanyAddress = isCompanyAddress;
    }

    /**
     * Sets the date of birth of the customer.
     *
     * @param dateOfBirth The date of birth of the customer.
     */
    setDateOfBirth(dateOfBirth: string) {
        this.dateOfBirth = dateOfBirth;
    }

    /**
     * Sets the email address of the customer.
     *
     * @param email The email address of the customer.
     */
    setEmail(email: string) {
        this.email = email;
    }

    /**
     * Sets the telephone number of the customer.
     *
     * @param telephone The telephone number of the customer.
     */
    setTelephone(telephone: string) {
        this.telephone = telephone;
    }

    /**
     * Sets the timeslot for when the customer is reachable.
     *
     * @param timeslot The timeslot.
     */
    setTimeslot(timeslot: 'afternoon' | 'allday' | 'evening' | 'morning') {
        this.timeslot = timeslot;
    }

    /**
     * Sets whether the customer has accepted to receive newsletters.
     *
     * @param hasAcceptedNewsletter Indicates whether the customer has accepted to receive newsletters.
     */
    setHasAcceptedNewsletter(hasAcceptedNewsletter: boolean) {
        this.hasAcceptedNewsletter = hasAcceptedNewsletter;
    }

    /**
     * Sets whether the customer has accepted the legal terms.
     *
     * @param hasAcceptedLegalTerms Indicates whether the customer has accepted the legal terms.
     */
    setHasAcceptedLegalTerms(hasAcceptedLegalTerms: boolean) {
        this.hasAcceptedLegalTerms = hasAcceptedLegalTerms;
    }
}

export const orderDataService = new OrderDataService();